import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Grid,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    CircularProgress,
    Stack,
    Alert,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import SettingsRemoteIcon from '@mui/icons-material/SettingsRemote';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SyncIcon from '@mui/icons-material/Sync';
import CableIcon from '@mui/icons-material/Cable';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { axiosInstance } from '../../../api';

const CARD_SX = {
    p: 2.5,
    borderRadius: '16px',
    bgcolor: '#fff',
    border: '1px solid #eef0f4',
    boxShadow: '0 4px 20px rgba(17,24,39,0.05)',
};

const BiometricDashboard = ({ onOpenWizard, onOpenGuide }) => {
    const [devices, setDevices] = useState([]);
    const [connectors, setConnectors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [testingId, setTestingId] = useState(null);
    const [syncingId, setSyncingId] = useState(null);
    const [alertInfo, setAlertInfo] = useState(null);

    // Connector Registration Modal State
    const [openConnectorModal, setOpenConnectorModal] = useState(false);
    const [connectorName, setConnectorName] = useState('');
    const [createdTokenInfo, setCreatedTokenInfo] = useState(null);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [devRes, connRes] = await Promise.all([
                axiosInstance.get('/api/attendance/biometric/devices/'),
                axiosInstance.get('/api/attendance/biometric/connectors/'),
            ]);
            setDevices(devRes.data.devices || []);
            setConnectors(connRes.data.connectors || []);
        } catch (err) {
            console.error('Error fetching biometric data:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleTestConnection = async (deviceId) => {
        setTestingId(deviceId);
        setAlertInfo(null);
        try {
            const res = await axiosInstance.post(`/api/attendance/biometric/devices/${deviceId}/test-connection/`);
            const cr = res.data.connection_result;
            if (cr.success) {
                setAlertInfo({
                    type: 'success',
                    message: `Connection successful (${cr.latency_ms}ms) - Firmware: ${cr.firmware_version || 'N/A'}${cr.is_simulation ? ' [Simulation Mode]' : ''}`,
                });
            } else {
                setAlertInfo({
                    type: 'warning',
                    message: `Connection status: ${cr.status} - ${cr.error}`,
                });
            }
            fetchData();
        } catch (err) {
            setAlertInfo({ type: 'error', message: 'Failed to test device connectivity.' });
        } finally {
            setTestingId(null);
        }
    };

    const handleSyncDevice = async (deviceId) => {
        setSyncingId(deviceId);
        setAlertInfo(null);
        try {
            const res = await axiosInstance.post(`/api/attendance/biometric/devices/${deviceId}/sync/`);
            setAlertInfo({
                type: 'success',
                message: `Sync finished: ${res.data.records_staged} punch(es) staged, ${res.data.duplicates_skipped} duplicates skipped, ${res.data.unmapped_count} unmapped.`,
            });
            fetchData();
        } catch (err) {
            setAlertInfo({ type: 'error', message: 'Sync failed: ' + (err.response?.data?.error || err.message) });
        } finally {
            setSyncingId(null);
        }
    };

    const handleCreateConnector = async () => {
        if (!connectorName.trim()) return;
        try {
            const res = await axiosInstance.post('/api/attendance/biometric/connectors/', { name: connectorName });
            setCreatedTokenInfo(res.data);
            setConnectorName('');
            fetchData();
        } catch (err) {
            alert('Failed to register connector.');
        }
    };

    const getStatusChip = (status, isSim) => {
        if (status === 'ONLINE') {
            return <Chip icon={<CheckCircleIcon />} label={isSim ? "Simulation Online" : "Online"} size="small" color="success" sx={{ fontWeight: 700 }} />;
        }
        if (status === 'OFFLINE' || status === 'TIMEOUT' || status === 'ERROR') {
            return <Chip icon={<ErrorOutlineIcon />} label={status} size="small" color="error" sx={{ fontWeight: 700 }} />;
        }
        return <Chip label={status || 'Unknown'} size="small" sx={{ fontWeight: 600, bgcolor: '#f3f4f6' }} />;
    };

    return (
        <Box sx={{ mt: 2 }}>
            {alertInfo && (
                <Alert severity={alertInfo.type} sx={{ mb: 2, borderRadius: '12px' }} onClose={() => setAlertInfo(null)}>
                    {alertInfo.message}
                </Alert>
            )}

            {/* Metrics Row */}
            <Grid container spacing={2.5} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper elevation={0} sx={CARD_SX}>
                        <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 700, textTransform: 'uppercase' }}>
                            Registered Devices
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 800, color: '#111827', mt: 0.5 }}>
                            {devices.length}
                        </Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper elevation={0} sx={CARD_SX}>
                        <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 700, textTransform: 'uppercase' }}>
                            Online / Simulated
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 800, color: '#10b981', mt: 0.5 }}>
                            {devices.filter((d) => d.connection_status === 'ONLINE').length}
                        </Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper elevation={0} sx={CARD_SX}>
                        <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 700, textTransform: 'uppercase' }}>
                            LAN Connectors
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 800, color: '#2563eb', mt: 0.5 }}>
                            {connectors.length}
                        </Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper elevation={0} sx={CARD_SX}>
                        <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 700, textTransform: 'uppercase' }}>
                            Active Connectors
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 800, color: '#8b5cf6', mt: 0.5 }}>
                            {connectors.filter((c) => c.heartbeat_status === 'ONLINE').length}
                        </Typography>
                    </Paper>
                </Grid>
            </Grid>

            {/* Actions Bar */}
            <Paper elevation={0} sx={{ ...CARD_SX, mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#111827' }}>
                        Biometric Hardware Management
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#6b7280' }}>
                        Manage on-premise attendance devices and isolated punch staging.
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1.5}>
                    <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchData} sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}>
                        Refresh
                    </Button>
                    <Button variant="outlined" startIcon={<CableIcon />} onClick={() => setOpenConnectorModal(true)} sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}>
                        Local Connectors
                    </Button>
                    <Button variant="outlined" startIcon={<HelpOutlineIcon />} onClick={onOpenGuide} sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}>
                        Integration Guide
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={onOpenWizard}
                        sx={{
                            borderRadius: '10px',
                            textTransform: 'none',
                            fontWeight: 700,
                            background: 'linear-gradient(135deg,#3b82f6,#2f6bff)',
                            '&:hover': { background: 'linear-gradient(135deg,#2f6bff,#2557d6)' },
                        }}
                    >
                        Add Device
                    </Button>
                </Stack>
            </Paper>

            {/* Devices Table */}
            <Paper elevation={0} sx={{ ...CARD_SX, p: 0, overflow: 'hidden' }}>
                <TableContainer>
                    <Table>
                        <TableHead sx={{ bgcolor: '#f9fafb' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700, color: '#374151' }}>Device Name & Identifier</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: '#374151' }}>Manufacturer & Model</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: '#374151' }}>Branch / Location</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: '#374151' }}>Connection</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: '#374151' }}>Status</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: '#374151' }}>Last Sync</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, color: '#374151' }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                                        <CircularProgress size={36} />
                                    </TableCell>
                                </TableRow>
                            ) : devices.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                                        <SettingsRemoteIcon sx={{ fontSize: 48, color: '#9ca3af', mb: 1 }} />
                                        <Typography variant="body1" sx={{ fontWeight: 700, color: '#374151' }}>
                                            No biometric devices registered
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#6b7280', mb: 2 }}>
                                            Connect your ZKTeco, eSSL, Matrix, Hikvision devices or test with the Virtual Simulator.
                                        </Typography>
                                        <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={onOpenWizard} sx={{ borderRadius: '8px', textTransform: 'none' }}>
                                            Launch Setup Wizard
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                devices.map((d) => (
                                    <TableRow key={d.id} hover>
                                        <TableCell>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#111827' }}>
                                                {d.name}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: '#6b7280' }}>
                                                ID: {d.device_identifier}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151' }}>
                                                {d.manufacturer_name}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: '#6b7280' }}>
                                                {d.model_name}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{d.branch_name || 'All Branches'}</Typography>
                                            <Typography variant="caption" sx={{ color: '#6b7280' }}>{d.location || 'N/A'}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={d.connection_method} size="small" variant="outlined" sx={{ fontWeight: 600, fontSize: '0.75rem' }} />
                                            {d.ip_address && (
                                                <Typography variant="caption" sx={{ display: 'block', color: '#6b7280', mt: 0.5 }}>
                                                    {d.ip_address}:{d.port}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {getStatusChip(d.connection_status, d.connection_method === 'SIMULATOR')}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {d.last_sync_at ? new Date(d.last_sync_at).toLocaleString() : 'Never'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                <Tooltip title="Test Connection">
                                                    <span>
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            onClick={() => handleTestConnection(d.id)}
                                                            disabled={testingId === d.id}
                                                            sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600 }}
                                                        >
                                                            {testingId === d.id ? <CircularProgress size={16} /> : 'Test'}
                                                        </Button>
                                                    </span>
                                                </Tooltip>
                                                <Tooltip title="Trigger Manual Sync">
                                                    <span>
                                                        <Button
                                                            size="small"
                                                            variant="contained"
                                                            startIcon={<SyncIcon />}
                                                            onClick={() => handleSyncDevice(d.id)}
                                                            disabled={syncingId === d.id}
                                                            sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600 }}
                                                        >
                                                            {syncingId === d.id ? <CircularProgress size={16} color="inherit" /> : 'Sync'}
                                                        </Button>
                                                    </span>
                                                </Tooltip>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Local Connector Registration Modal */}
            <Dialog open={openConnectorModal} onClose={() => { setOpenConnectorModal(false); setCreatedTokenInfo(null); }} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 800 }}>Manage Local LAN Connectors</DialogTitle>
                <DialogContent dividers>
                    {createdTokenInfo ? (
                        <Box sx={{ py: 1 }}>
                            <Alert severity="success" sx={{ mb: 2, borderRadius: '10px' }}>
                                Connector created successfully! Copy this authentication token now. For security, it will not be displayed again.
                            </Alert>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                                Connector ID:
                            </Typography>
                            <TextField fullWidth size="small" value={createdTokenInfo.connector_id} InputProps={{ readOnly: true }} sx={{ mb: 2 }} />
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                                Connector Secret Token:
                            </Typography>
                            <TextField fullWidth size="small" multiline rows={2} value={createdTokenInfo.connector_token} InputProps={{ readOnly: true }} />
                        </Box>
                    ) : (
                        <Box sx={{ py: 1 }}>
                            <Typography variant="body2" sx={{ color: '#4b5563', mb: 2 }}>
                                The Local Connector Agent runs inside your office LAN, polls biometric devices, and pushes normalized punches via outbound HTTPS.
                            </Typography>
                            <TextField
                                fullWidth
                                label="Connector Name (e.g. Bangalore HO Gateway)"
                                size="small"
                                value={connectorName}
                                onChange={(e) => setConnectorName(e.target.value)}
                                sx={{ mb: 2 }}
                            />
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2, mb: 1 }}>
                                Active Connectors:
                            </Typography>
                            {connectors.length === 0 ? (
                                <Typography variant="caption" sx={{ color: '#9ca3af' }}>No registered connectors.</Typography>
                            ) : (
                                connectors.map((c) => (
                                    <Box key={c.id} sx={{ p: 1.5, mb: 1, bgcolor: '#f9fafb', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Box>
                                            <Typography variant="body2" sx={{ fontWeight: 700 }}>{c.name}</Typography>
                                            <Typography variant="caption" sx={{ color: '#6b7280' }}>Token prefix: {c.token_prefix}... | IP: {c.ip_address || 'Unreported'}</Typography>
                                        </Box>
                                        <Chip label={c.heartbeat_status} size="small" color={c.heartbeat_status === 'ONLINE' ? 'success' : 'default'} sx={{ fontWeight: 700 }} />
                                    </Box>
                                ))
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    {createdTokenInfo ? (
                        <Button variant="contained" onClick={() => { setOpenConnectorModal(false); setCreatedTokenInfo(null); }} sx={{ borderRadius: '8px', textTransform: 'none' }}>
                            Done
                        </Button>
                    ) : (
                        <>
                            <Button onClick={() => setOpenConnectorModal(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
                            <Button variant="contained" onClick={handleCreateConnector} disabled={!connectorName.trim()} sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700 }}>
                                Register Connector
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default BiometricDashboard;

